import { mapStateColors } from "@/common/constants";
import { DateHelper } from "@/utility/dateHelper.js";
import { date } from "@/components/mixins/date";
import { wrapStatus } from "@/stores/helpers/machineStatusHelper";
import Vue from "vue";

const ensureFirstPoint = (data, machineStatus) => {
  const result = data;
  let firstPoint = undefined;

  if (result) {
    if (result.length > 0) {
      firstPoint = result[0];
    }

    const startOfDay = date.methods.objDateTodayStartOf("day", null);
    let firstStartTime = undefined;
    let newPoint = true;
    let status = machineStatus || "unknown";

    if (firstPoint) {
      firstStartTime = firstPoint.StartTime && new Date(firstPoint.StartTime);
      status = "unknown";

      if (firstStartTime && date.methods.diffSeconds(startOfDay, firstStartTime) < 0) {
        firstPoint.StartTime = startOfDay.toISOString();
        newPoint = false;
      }
    }

    if (newPoint) {
      result.unshift(fakeOriginal(startOfDay, firstStartTime, status));
    }
  }
  return result;
};

export const insertPointInData = (point, accumulator) => {
  let currentStatus = undefined;
  let normalizedPoint = undefined;

  if (accumulator) {
    const { lastStatus, series, normalizedPoints, exportData } = accumulator;

    if (point && series && normalizedPoints && exportData) {
      normalizedPoint = normalizePoint(point);
      currentStatus = normalizedPoint && normalizedPoint.status;

      const previousPoints = normalizedPoints[lastStatus];
      let previousPoint = undefined;

      if (previousPoints && previousPoints.length > 0) {
        previousPoint = previousPoints[previousPoints.length - 1];
      }

      const ok = isValidPoint(normalizedPoint, previousPoint);

      if (ok) {
        if (lastStatus) {
          const lastSerieData = series[lastStatus];

          if (lastSerieData && lastSerieData.length > 0) {
            const lastIndex = lastSerieData.length - 1;
            const lastValue = lastSerieData[lastIndex - 1];

            if (normalizedPoint.from >= lastValue[0]) {
              updatePoint(previousPoint, normalizedPoint);

              lastSerieData[lastIndex] = [normalizedPoint.from, 0];
            }
          }
        }

        if (currentStatus && currentStatus in normalizedPoints) {
          let serieData = series[currentStatus];

          if (!serieData) {
            series[currentStatus] = [];
            serieData = series[currentStatus];
          }

          if (serieData) {
            serieData.push([normalizedPoint.from, 1]);
            serieData.push([normalizedPoint.to, 0]);
          }

          accumulator.lastStatus = currentStatus;

          normalizedPoints[currentStatus].push(normalizedPoint);

          exportData.push({
            col0: date.methods.getCustomFormatDate(normalizedPoint.from, "yyyy/MM/dd HH:mm:ss"),
            col1: normalizedPoint.original.EndTime
              ? date.methods.getCustomFormatDate(normalizedPoint.to, "yyyy/MM/dd HH:mm:ss")
              : "",
            col2: normalizedPoint.original.Status || currentStatus.toUpperCase(),
            col3: DateHelper.getFancyTimeHHMMSS(normalizedPoint.amount)
          });
        } else {
          console.error("[Error] Status not valid!", normalizedPoint);
        }
      }
    } else {
      console.error("[Error] Something wrong!", point, accumulator);
    }
  } else {
    console.error("[Error] Accumulator required!", accumulator);
  }

  return accumulator;
};

const getTooltip = normalizedPoint => {
  let withAmount = false;
  let withoutAmount = false;

  if (normalizedPoint) {
    const { status, from, to, amount, isLast } = normalizedPoint;

    if (status !== "undefined") {
      const color = wrapStatus(status);
      const startText = DateHelper.formatTimeWithSeconds(from, Vue.i18n.locale());
      const endText = DateHelper.formatTimeWithSeconds(to, Vue.i18n.locale());
      const amountText = DateHelper.getFancyTimeFormat(amount);
      const statusText = Vue.i18n.translate("status." + color);

      withAmount = `<b>${statusText}</b><br/>${startText}${
        isLast ? "" : `<br/>${endText}`
      }<br/>${amountText}`;

      if (color) {
        withoutAmount = `<b>${statusText}</b>, ${startText}`;
      } else {
        withoutAmount = `<b>${status}</b>, ${date.methods.getCustomFormatDate(from, "HH:mm:ss")}`;
      }
    }
  }

  return (
    withAmount && {
      withAmount,
      withoutAmount
    }
  );
};

const normalizePoint = (value, forceTo = false) => {
  const startTime = value.StartTime || value.ts;
  const endTime = value.EndTime || new Date();
  const from = (startTime && new Date(startTime).getTime()) || undefined;
  let to = new Date(endTime).getTime();
  const diff = to - from;

  if ((forceTo && diff < 0) || (-60000 < diff && diff < 0)) {
    to = from + 1000;
  }

  const status = (value.Status || value.v || value.status || "unknown").toLowerCase();
  const amount = (value && value.AmountSingle) || 0;
  const isLast = !value.EndTime;
  const result = { original: value, from, to, status, amount, isLast };

  result.tooltip = getTooltip(result);

  return result;
};

const updatePoint = (oldPoint, nextPoint) => {
  if (oldPoint) {
    const newPoint = fakeOriginal(
      new Date(oldPoint.from),
      new Date(nextPoint.from),
      oldPoint.status
    );
    const newNormalizedPoint = normalizePoint(newPoint);

    for (const [key, value] of Object.entries(newNormalizedPoint)) {
      oldPoint[key] = value;
    }
  }

  return oldPoint;
};

export const fakeOriginal = (fromDate, toDate, status) => {
  return {
    AmountSingle: amountValue(fromDate, toDate),
    EndTime: (toDate && toDate.toISOString()) || null,
    StartTime: fromDate.toISOString(),
    Status: (status || "").toUpperCase()
  };
};

const amountValue = (fromDate, toDate) => {
  return fromDate && toDate ? date.methods.diffSeconds(fromDate, toDate) : 0;
};

const validatePoint = (newPoint, prevPoint) => {
  const invalidAmount = !newPoint.isLast && newPoint.amount <= 0;
  const newRangeNotValid = newPoint.to <= newPoint.from;
  const pointNotConsistentWithPrevious = prevPoint
    ? (!prevPoint.isLast && newPoint.from < prevPoint.to) || newPoint.from <= prevPoint.from
    : false;
  let result = undefined;

  if (invalidAmount) {
    result = "invalidAmount";
  } else if (newRangeNotValid) {
    result = "newRangeNotValid";
  } else if (pointNotConsistentWithPrevious) {
    result = "pointNotConsistentWithPrevious";
  }

  return result;
};

const isValidPoint = (newPoint, prevPoint) => {
  const invalid = validatePoint(newPoint, prevPoint);

  if (invalid && invalid !== "invalidAmount") {
    console.error(`[Error] Point not valid! Validation: ${invalid}`, newPoint, prevPoint);
  }

  return !invalid;
};

const dataToSeries = async elements => {
  return new Promise((resolve, reject) => {
    const normalizedPoints = Object.keys(mapStateColors).reduce((acc, key) => {
      acc[key] = [];
      return acc;
    }, {});

    const exportData = [
      {
        col0: "Start",
        col1: "End",
        col2: "Status",
        col3: "Amount"
      }
    ];

    (elements || [])
      .reduce(
        async (previousPromise, element) => {
          const accumulator = await previousPromise;
          return Promise.resolve(insertPointInData(element, accumulator));
        },
        Promise.resolve({
          normalizedPoints,
          lastStatus: undefined,
          exportData,
          series: {}
        })
      )
      .then(resolve)
      .catch(reject);
  });
};

export const mapDataToStandardTimelineData = async (data, status) => {
  let items = data;

  if (!items) {
    items = [];
  }

  // const numberOfPoints = items.length;
  // const start = new Date();

  items = ensureFirstPoint(items, status);
  const result = await dataToSeries(items);

  // console.info(
  //   `[Info] Number of points: ${numberOfPoints}. Execution time: ${new Date() - start}ms.`
  // );

  return result;
};

export const mapDataToTimelineData = async data => {
  let items = data;

  if (!items) {
    items = [];
  }

  // const numberOfPoints = items.length;
  // const start = new Date();

  const result = await dataToSeries(items);

  // console.info(
  //   `[Info] Number of points: ${numberOfPoints}. Execution time: ${new Date() - start}ms.`
  // );

  return result;
};

const addUndefinedPoints = (points, addLast, startTime, endTime) => {
  let result = undefined;

  if (points && points.length > 0) {
    const undefinedPoints = [];
    const undefinedSeries = [];
    const lastPoint = points[points.length - 1];
    const firstPoint = points[0];
    const rangeStart = new Date(startTime);

    if (firstPoint) {
      const firstStartTime = new Date(firstPoint.from);
      if (date.methods.diffSeconds(firstStartTime, rangeStart) > 0) {
        const undefinedFirstPoint = normalizePoint(
          fakeOriginal(rangeStart, firstStartTime, "undefined")
        );

        undefinedPoints.unshift(undefinedFirstPoint);
        undefinedSeries.unshift([undefinedFirstPoint.to, 0]);
        undefinedSeries.unshift([undefinedFirstPoint.from, 1]);
      }
    }

    if (lastPoint && addLast) {
      const undefinedLastPoint = normalizePoint(
        {
          status: "undefined",
          StartTime: lastPoint.to,
          EndTime: endTime
        },
        true
      );

      undefinedPoints.push(undefinedLastPoint);
      undefinedSeries.push([undefinedLastPoint.from, 1]);
      undefinedSeries.push([undefinedLastPoint.to, 0]);
    }

    result = {
      normalizedPoints: {
        undefined: undefinedPoints
      },
      series: {
        undefined: undefinedSeries
      }
    };
  } else {
    const undefinedPoint = normalizePoint(
      {
        status: "undefined",
        StartTime: startTime,
        EndTime: endTime
      },
      true
    );

    result = {
      normalizedPoints: {
        undefined: [undefinedPoint]
      },
      series: {
        undefined: [
          [undefinedPoint.from, 1],
          [undefinedPoint.to, 0]
        ]
      }
    };
  }

  return result;
};

export const timelineFiltered = (timeline, filter, startTime, endTime) => {
  let result = undefined;

  if (timeline && filter) {
    const normalizedPoints = timeline.normalizedPoints[filter];
    const undefinedResult = addUndefinedPoints(
      normalizedPoints,
      timeline.lastStatus !== filter,
      startTime,
      endTime
    );

    if (undefinedResult) {
      result = {
        ...timeline,
        exportData: timeline.exportData.filter(
          (el, index) => index === 0 || el.col2 === filter.toUpperCase()
        ),
        normalizedPoints: {
          [filter]: normalizedPoints,
          undefined: undefinedResult.normalizedPoints.undefined
        },
        series: {
          [filter]: timeline.series[filter],
          undefined: undefinedResult.series.undefined
        }
      };
    }
  }

  return result;
};
